import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import './MeetingCreate.css';

const STORAGE_KEY = 'meeting_create_data';

export const MeetingCreateStep1 = () => {
    const navigate = useNavigate();

    // sessionStorage에서 저장된 데이터 로드
    const loadSavedData = () => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : {};
    };

    const savedData = loadSavedData();
    const meetingType = savedData.meetingType || 'DAY';
    const isTravel = meetingType === 'TRAVEL';

    const [meetingName, setMeetingName] = useState(savedData.meetingName || '');
    const [description, setDescription] = useState(savedData.description || '');
    const [isRegularMeeting, setIsRegularMeeting] = useState(savedData.isRegularMeeting || false);
    const [repeatType, setRepeatType] = useState<'WEEKLY' | 'MONTHLY'>(savedData.repeatType || 'WEEKLY');
    const [repeatInterval, setRepeatInterval] = useState(savedData.repeatInterval || 1);

    // 데이터 변경 시 sessionStorage에 저장
    useEffect(() => {
        const dataToSave = {
            ...savedData,
            meetingName,
            description,
            isRegularMeeting,
            repeatType: isRegularMeeting ? repeatType : null,
            repeatInterval: isRegularMeeting ? repeatInterval : null
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }, [meetingName, description, isRegularMeeting, repeatType, repeatInterval]);

    const isNextEnabled = meetingName.trim().length > 0;

    const handleNext = () => {
        if (isNextEnabled) {
            // Save data and navigate to step 3
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
                ...savedData,
                meetingName,
                description,
                isRegularMeeting,
                repeatType: isRegularMeeting ? repeatType : null,
                repeatInterval: isRegularMeeting ? repeatInterval : null
            }));
            // 여행이면 여행지 선택 화면으로, 당일이면 기존 날짜/장소 화면으로
            if (isTravel) {
                navigate('/meeting/create/travel-destination');
            } else {
                navigate('/meeting/create/step3');
            }
        }
    };

    return (
        <div className="meeting-create-wrapper">
            <Header title="모임 만들기" isSubPage />

            <div className="meeting-create-content">
                {/* Progress Indicator - 3 Steps */}
                <div className="progress-indicator">
                    <div className="progress-step completed">1</div>
                    <div className="progress-line active"></div>
                    <div className="progress-step active">2</div>
                    <div className="progress-line"></div>
                    <div className="progress-step">3</div>
                </div>

                <div className="progress-labels three-steps">
                    <span className="progress-label completed">모임 유형</span>
                    <span className="progress-label active">모임 정보</span>
                    <span className="progress-label">일정 설정</span>
                </div>

                {/* Title Section */}
                <div className="meeting-create-title-section">
                    <div className="meeting-type-badge">
                        {isTravel ? '✈️ 여행' : '🍽️ 당일 모임'}
                    </div>
                    <h1 className="meeting-create-title">
                        {isTravel ? '어떤 여행을 계획하고 있나요?' : '어떤 모임을 만드나요?'}
                    </h1>
                    <p className="meeting-create-subtitle">
                        {isTravel
                            ? '여행의 이름과 간단한 설명을 입력해주세요.'
                            : '나중에 언제든지 수정할 수 있어요.'}
                    </p>
                </div>

                {/* Form Section */}
                <div className="meeting-create-form">
                    {/* Meeting Name */}
                    <div className="form-group">
                        <label className="form-label">
                            모임명 <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="예) 맛집 탐방"
                            value={meetingName}
                            onChange={(e) => setMeetingName(e.target.value.slice(0, 20))}
                            maxLength={20}
                        />
                        <div className="form-hint">
                            <span className="char-count">{meetingName.length}/20</span>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="form-group">
                        <label className="form-label">설명 (선택)</label>
                        <textarea
                            className="form-textarea"
                            placeholder="모임에 대한 간단한 설명을 적어주세요."
                            value={description}
                            onChange={(e) => setDescription(e.target.value.slice(0, 200))}
                            maxLength={200}
                            rows={4}
                        />
                        <div className="form-hint">
                            <span className="char-count">{description.length}/200</span>
                        </div>
                    </div>

                    {/* Regular Meeting Toggle - 당일 모임에서만 표시 */}
                    {!isTravel && (
                        <div className="form-group">
                            <div className="toggle-container">
                                <div className="toggle-info">
                                    <span className="toggle-label">정기 모임 설정</span>
                                    <span className="toggle-description">매주/매월 반복되는 모임인가요?</span>
                                </div>
                                <button
                                    className={`toggle-switch ${isRegularMeeting ? 'active' : ''}`}
                                    onClick={() => setIsRegularMeeting(!isRegularMeeting)}
                                >
                                    <div className="toggle-knob"></div>
                                </button>
                            </div>

                            {/* Repeat Options - shown when toggle is ON */}
                            {isRegularMeeting && (
                                <div className="repeat-options">
                                    <label className="form-label">반복 주기</label>
                                    <div className="repeat-buttons">
                                        <button
                                            className={`repeat-button ${repeatType === 'WEEKLY' && repeatInterval === 1 ? 'selected' : ''}`}
                                            onClick={() => { setRepeatType('WEEKLY'); setRepeatInterval(1); }}
                                        >
                                            매주
                                        </button>
                                        <button
                                            className={`repeat-button ${repeatType === 'WEEKLY' && repeatInterval === 2 ? 'selected' : ''}`}
                                            onClick={() => { setRepeatType('WEEKLY'); setRepeatInterval(2); }}
                                        >
                                            격주
                                        </button>
                                        <button
                                            className={`repeat-button ${repeatType === 'MONTHLY' ? 'selected' : ''}`}
                                            onClick={() => { setRepeatType('MONTHLY'); setRepeatInterval(1); }}
                                        >
                                            매월
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Next Button */}
            <div className="meeting-create-footer">
                <Button
                    text="다음으로"
                    disabled={!isNextEnabled}
                    onPress={handleNext}
                />
            </div>
        </div>
    );
};
